/**
 * Directly adapted from PlayCanvas Engine's GSplat source format chunks at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 *
 * 这些字符串是 WebGL GLSL 与 WebGPU TSL/WGSL source-reader 的共同冻结真源。
 */

export const GSPLAT_UNCOMPRESSED_GLSL = /* glsl */ `
uint tAw;
vec4 tBcached;

vec4 unpackRotation(vec3 data) {
    return vec4(data.xyz, sqrt(max(0.0, 1.0 - dot(data, data))));
}

vec3 getCenter() {
    uvec4 tA = loadTransformA();
    tAw = tA.w;
    tBcached = loadTransformB();
    return uintBitsToFloat(tA.xyz);
}

vec4 getColor() { return loadSplatColor(); }
vec4 getRotation() { return unpackRotation(vec3(unpackHalf2x16(tAw), tBcached.w)).wxyz; }
vec3 getScale() { return tBcached.xyz; }

#include "gsplatUncompressedSHVS"
`

export const GSPLAT_UNCOMPRESSED_WGSL = /* wgsl */ `
var<private> tAw: u32;
var<private> tBcached: vec4f;

fn unpackRotation(data: vec3f) -> vec4f {
    return vec4f(data.xyz, sqrt(max(0.0, 1.0 - dot(data, data))));
}

fn getCenter() -> vec3f {
    let tA: vec4<u32> = loadTransformA();
    tAw = tA.w;
    tBcached = loadTransformB();
    return bitcast<vec3f>(tA.xyz);
}

fn getColor() -> vec4f { return loadSplatColor(); }
fn getRotation() -> vec4f { return unpackRotation(vec3f(unpack2x16float(tAw), tBcached.w)).wxyz; }
fn getScale() -> vec3f { return tBcached.xyz; }

#include "gsplatUncompressedSHVS"
`

export const GSPLAT_COMPRESSED_GLSL = /* glsl */ `
#include "gsplatPackingPS"

uniform highp sampler2D chunkTexture;

vec4 chunkDataA;
vec4 chunkDataB;
vec4 chunkDataC;
vec4 chunkDataD;
vec4 chunkDataE;
uvec4 packedData;

vec3 unpack111011(uint bits) {
    return vec3(
        float(bits >> 21u) / 2047.0,
        float((bits >> 11u) & 0x3ffu) / 1023.0,
        float(bits & 0x7ffu) / 2047.0
    );
}

const float norm = sqrt(2.0);

vec4 unpackRotation(uint bits) {
    float a = (float((bits >> 20u) & 0x3ffu) / 1023.0 - 0.5) * norm;
    float b = (float((bits >> 10u) & 0x3ffu) / 1023.0 - 0.5) * norm;
    float c = (float(bits & 0x3ffu) / 1023.0 - 0.5) * norm;
    float m = sqrt(1.0 - (a * a + b * b + c * c));

    uint mode = bits >> 30u;
    if (mode == 0u) return vec4(m, a, b, c);
    if (mode == 1u) return vec4(a, m, b, c);
    if (mode == 2u) return vec4(a, b, m, c);
    return vec4(a, b, c, m);
}

vec3 getCenter() {
    uint w = uint(textureSize(chunkTexture, 0).x) / 5u;
    uint chunkId = splat.index / 256u;
    ivec2 chunkUV = ivec2((chunkId % w) * 5u, chunkId / w);
    chunkDataA = texelFetch(chunkTexture, chunkUV, 0);
    chunkDataB = texelFetch(chunkTexture, chunkUV + ivec2(1, 0), 0);
    chunkDataC = texelFetch(chunkTexture, chunkUV + ivec2(2, 0), 0);
    chunkDataD = texelFetch(chunkTexture, chunkUV + ivec2(3, 0), 0);
    chunkDataE = texelFetch(chunkTexture, chunkUV + ivec2(4, 0), 0);
    packedData = loadPackedTexture();
    return mix(chunkDataA.xyz, vec3(chunkDataA.w, chunkDataB.xy), unpack111011(packedData.x));
}

vec4 getColor() {
    vec4 r = unpack8888(packedData.w);
    return vec4(mix(chunkDataD.xyz, vec3(chunkDataD.w, chunkDataE.xy), r.rgb), r.w);
}

vec4 getRotation() { return unpackRotation(packedData.y); }
vec3 getScale() {
    return exp(mix(vec3(chunkDataB.zw, chunkDataC.x), chunkDataC.yzw, unpack111011(packedData.z)));
}

#include "gsplatCompressedSHVS"
`

export const GSPLAT_COMPRESSED_WGSL = /* wgsl */ `
#include "gsplatPackingPS"

var chunkTexture: texture_2d<uff>;

var<private> chunkDataA: vec4f;
var<private> chunkDataB: vec4f;
var<private> chunkDataC: vec4f;
var<private> chunkDataD: vec4f;
var<private> chunkDataE: vec4f;
var<private> packedData: vec4u;

fn unpack111011(bits: u32) -> vec3f {
    return (vec3f((vec3<u32>(bits) >> vec3<u32>(21u, 11u, 0u)) & vec3<u32>(0x7ffu, 0x3ffu, 0x7ffu))) / vec3f(2047.0, 1023.0, 2047.0);
}

const norm_const: f32 = sqrt(2.0);

fn unpackRotation(bits: u32) -> vec4f {
    let a = (f32((bits >> 20u) & 0x3ffu) / 1023.0 - 0.5) * norm_const;
    let b = (f32((bits >> 10u) & 0x3ffu) / 1023.0 - 0.5) * norm_const;
    let c = (f32(bits & 0x3ffu) / 1023.0 - 0.5) * norm_const;
    let m = sqrt(1.0 - (a * a + b * b + c * c));
    let mode = bits >> 30u;
    if (mode == 0u) { return vec4f(m, a, b, c); }
    if (mode == 1u) { return vec4f(a, m, b, c); }
    if (mode == 2u) { return vec4f(a, b, m, c); }
    return vec4f(a, b, c, m);
}

fn getCenter() -> vec3f {
    let tex_size_u = textureDimensions(chunkTexture, 0);
    let w: u32 = tex_size_u.x / 5u;
    let chunkId: u32 = splat.index / 256u;
    let chunkUV: vec2<i32> = vec2<i32>(i32((chunkId % w) * 5u), i32(chunkId / w));
    chunkDataA = textureLoad(chunkTexture, chunkUV + vec2<i32>(0, 0), 0);
    chunkDataB = textureLoad(chunkTexture, chunkUV + vec2<i32>(1, 0), 0);
    chunkDataC = textureLoad(chunkTexture, chunkUV + vec2<i32>(2, 0), 0);
    chunkDataD = textureLoad(chunkTexture, chunkUV + vec2<i32>(3, 0), 0);
    chunkDataE = textureLoad(chunkTexture, chunkUV + vec2<i32>(4, 0), 0);
    packedData = loadPackedTexture();
    return mix(chunkDataA.xyz, vec3f(chunkDataA.w, chunkDataB.xy), unpack111011(packedData.x));
}

fn getColor() -> vec4f {
    let r = unpack8888(packedData.w);
    return vec4f(mix(chunkDataD.xyz, vec3f(chunkDataD.w, chunkDataE.xy), r.rgb), r.w);
}

fn getRotation() -> vec4f { return unpackRotation(packedData.y); }
fn getScale() -> vec3f {
    return exp(mix(vec3f(chunkDataB.zw, chunkDataC.x), chunkDataC.yzw, unpack111011(packedData.z)));
}

#include "gsplatCompressedSHVS"
`

export const GSPLAT_SOG_GLSL = /* glsl */ `
uniform vec3 means_mins;
uniform vec3 means_maxs;

#ifndef SOG_V2
    uniform vec3 scales_mins;
    uniform vec3 scales_maxs;
    uniform vec4 sh0_mins;
    uniform vec4 sh0_maxs;
#endif

const float SH_C0 = 0.28209479177387814;
const float norm = sqrt(2.0);

#ifdef SOG_V2
    float lutScales(int b) { return texelFetch(sogCodebook, ivec2(b, 0), 0).r; }
    float lutSh0(int b)    { return texelFetch(sogCodebook, ivec2(b, 0), 0).g; }
    float lutShN(int b)    { return texelFetch(sogCodebook, ivec2(b, 0), 0).b; }
#endif

#ifndef GSPLAT_WORKBUFFER_GEOMETRY
vec3 getCenter() {
    vec3 l = texelFetch(means_l, splat.uv, 0).xyz;
    vec3 u = texelFetch(means_u, splat.uv, 0).xyz;
    vec3 n = (l + u * 256.0) / 257.0;
    vec3 v = mix(means_mins, means_maxs, n);
    return sign(v) * (exp(abs(v)) - 1.0);
}

vec4 getRotation() {
    vec4 q = texelFetch(quats, splat.uv, 0);
    uint qmode = uint(q.w * 255.0 + 0.5) - 252u;
    vec3 abc = (q.xyz - 0.5) * norm;
    float d = sqrt(max(0.0, 1.0 - dot(abc, abc)));
    return (qmode == 0u) ? vec4(d, abc) :
           ((qmode == 1u) ? vec4(abc.x, d, abc.yz) :
           ((qmode == 2u) ? vec4(abc.xy, d, abc.z) : vec4(abc, d)));
}

vec3 getScale() {
    vec3 s = texelFetch(scales, splat.uv, 0).xyz;
    #ifdef SOG_V2
        ivec3 i = ivec3(s * 255.0 + 0.5);
        vec3 logS = vec3(lutScales(i.x), lutScales(i.y), lutScales(i.z));
    #else
        vec3 logS = mix(scales_mins, scales_maxs, s);
    #endif
    return exp(logS);
}
#endif

vec4 getColor() {
    vec4 c = texelFetch(sh0, splat.uv, 0);
    #ifdef SOG_V2
        ivec3 i = ivec3(c.xyz * 255.0 + 0.5);
        vec3 rgb = vec3(lutSh0(i.x), lutSh0(i.y), lutSh0(i.z));
        float alpha = c.w;
    #else
        vec3 rgb = mix(sh0_mins.xyz, sh0_maxs.xyz, c.xyz);
        float logitAlpha = mix(sh0_mins.w, sh0_maxs.w, c.w);
        float alpha = 1.0 / (1.0 + exp(-logitAlpha));
    #endif
    return vec4(vec3(0.5) + rgb * SH_C0, alpha);
}

#include "gsplatSogSHVS"
`

export const GSPLAT_SOG_WGSL = /* wgsl */ `
uniform means_mins: vec3f;
uniform means_maxs: vec3f;

#ifndef SOG_V2
    uniform scales_mins: vec3f;
    uniform scales_maxs: vec3f;
    uniform sh0_mins: vec4f;
    uniform sh0_maxs: vec4f;
#endif

const SH_C0: f32 = 0.28209479177387814;
const norm: f32 = sqrt(2.0);

#ifdef SOG_V2
    fn lutScales(b: i32) -> f32 { return textureLoad(sogCodebook, vec2i(b, 0), 0).r; }
    fn lutSh0(b: i32) -> f32    { return textureLoad(sogCodebook, vec2i(b, 0), 0).g; }
    fn lutShN(b: i32) -> f32    { return textureLoad(sogCodebook, vec2i(b, 0), 0).b; }
#endif

#ifndef GSPLAT_WORKBUFFER_GEOMETRY
fn getCenter() -> vec3f {
    let l = textureLoad(means_l, splat.uv, 0).xyz;
    let u = textureLoad(means_u, splat.uv, 0).xyz;
    let n = (l + u * 256.0) / 257.0;
    let v = mix(uniform.means_mins, uniform.means_maxs, n);
    return sign(v) * (exp(abs(v)) - 1.0);
}

fn getRotation() -> vec4f {
    let q = textureLoad(quats, splat.uv, 0);
    let qmode = u32(q.w * 255.0 + 0.5) - 252u;
    let abc = (q.xyz - 0.5) * norm;
    let d = sqrt(max(0.0, 1.0 - dot(abc, abc)));
    var quat: vec4f;
    if (qmode == 0u) {
        quat = vec4f(d, abc);
    } else if (qmode == 1u) {
        quat = vec4f(abc.x, d, abc.y, abc.z);
    } else if (qmode == 2u) {
        quat = vec4f(abc.x, abc.y, d, abc.z);
    } else {
        quat = vec4f(abc.x, abc.y, abc.z, d);
    }
    return quat;
}

fn getScale() -> vec3f {
    let s = textureLoad(scales, splat.uv, 0).xyz;
    var logS: vec3f;
    #ifdef SOG_V2
        let i = vec3i(s * 255.0 + 0.5);
        logS = vec3f(lutScales(i.x), lutScales(i.y), lutScales(i.z));
    #else
        logS = mix(uniform.scales_mins, uniform.scales_maxs, s);
    #endif
    return exp(logS);
}
#endif

fn getColor() -> vec4f {
    let c = textureLoad(sh0, splat.uv, 0);
    var rgb: vec3f;
    var alpha: f32;
    #ifdef SOG_V2
        let i = vec3i(c.xyz * 255.0 + 0.5);
        rgb = vec3f(lutSh0(i.x), lutSh0(i.y), lutSh0(i.z));
        alpha = c.w;
    #else
        rgb = mix(uniform.sh0_mins.xyz, uniform.sh0_maxs.xyz, c.xyz);
        let logitAlpha = mix(uniform.sh0_mins.w, uniform.sh0_maxs.w, c.w);
        alpha = 1.0 / (1.0 + exp(-logitAlpha));
    #endif
    return vec4f(vec3f(0.5) + rgb * SH_C0, alpha);
}

#include "gsplatSogSHVS"
`

export const GSPLAT_UNCOMPRESSED_SH_GLSL = /* glsl */ `
#if SH_BANDS > 0
vec3 unpack111011s(uint bits) {
    return vec3((uvec3(bits) >> uvec3(21u, 11u, 0u)) & uvec3(0x7ffu, 0x3ffu, 0x7ffu)) / vec3(2047.0, 1023.0, 2047.0) * 2.0 - 1.0;
}
void fetchScale(in uvec4 t, out float scale, out vec3 a, out vec3 b, out vec3 c) {
    scale = uintBitsToFloat(t.x); a = unpack111011s(t.y); b = unpack111011s(t.z); c = unpack111011s(t.w);
}
void fetch(in uvec4 t, out vec3 a, out vec3 b, out vec3 c, out vec3 d) {
    a = unpack111011s(t.x); b = unpack111011s(t.y); c = unpack111011s(t.z); d = unpack111011s(t.w);
}
void fetch(in uint t, out vec3 a) { a = unpack111011s(t); }
#if SH_BANDS == 1
void readSHData(out vec3 sh[3], out float scale) {
    fetchScale(loadSplatSH_1to3(), scale, sh[0], sh[1], sh[2]);
}
#elif SH_BANDS == 2
void readSHData(out vec3 sh[8], out float scale) {
    fetchScale(loadSplatSH_1to3(), scale, sh[0], sh[1], sh[2]);
    fetch(loadSplatSH_4to7(), sh[3], sh[4], sh[5], sh[6]);
    fetch(loadSplatSH_8to11().x, sh[7]);
}
#else
void readSHData(out vec3 sh[15], out float scale) {
    fetchScale(loadSplatSH_1to3(), scale, sh[0], sh[1], sh[2]);
    fetch(loadSplatSH_4to7(), sh[3], sh[4], sh[5], sh[6]);
    fetch(loadSplatSH_8to11(), sh[7], sh[8], sh[9], sh[10]);
    fetch(loadSplatSH_12to15(), sh[11], sh[12], sh[13], sh[14]);
}
#endif
#endif
`

export const GSPLAT_UNCOMPRESSED_SH_WGSL = /* wgsl */ `
#if SH_BANDS > 0
fn unpack111011s(bits: u32) -> vec3f {
    return (vec3f((vec3<u32>(bits) >> vec3<u32>(21u, 11u, 0u)) & vec3<u32>(0x7ffu, 0x3ffu, 0x7ffu)) / vec3f(2047.0, 1023.0, 2047.0)) * 2.0 - 1.0;
}
struct ScaleAndSH { scale: f32, a: vec3f, b: vec3f, c: vec3f };
fn fetchScale(t_in: vec4<u32>) -> ScaleAndSH {
    var result: ScaleAndSH;
    result.scale = bitcast<f32>(t_in.x); result.a = unpack111011s(t_in.y);
    result.b = unpack111011s(t_in.z); result.c = unpack111011s(t_in.w); return result;
}
struct SH { a: vec3f, b: vec3f, c: vec3f, d: vec3f };
fn fetch4(t_in: vec4<u32>) -> SH {
    var result: SH; result.a = unpack111011s(t_in.x); result.b = unpack111011s(t_in.y);
    result.c = unpack111011s(t_in.z); result.d = unpack111011s(t_in.w); return result;
}
fn fetch1(t_in: u32) -> vec3f { return unpack111011s(t_in); }
#if SH_BANDS == 1
fn readSHData(sh: ptr<function, array<half3, 3>>, scale: ptr<function, f32>) {
    let a = fetchScale(loadSplatSH_1to3()); *scale = a.scale;
    sh[0] = half3(a.a); sh[1] = half3(a.b); sh[2] = half3(a.c);
}
#elif SH_BANDS == 2
fn readSHData(sh: ptr<function, array<half3, 8>>, scale: ptr<function, f32>) {
    let a = fetchScale(loadSplatSH_1to3()); *scale = a.scale;
    sh[0] = half3(a.a); sh[1] = half3(a.b); sh[2] = half3(a.c);
    let b = fetch4(loadSplatSH_4to7());
    sh[3] = half3(b.a); sh[4] = half3(b.b); sh[5] = half3(b.c); sh[6] = half3(b.d);
    sh[7] = half3(fetch1(loadSplatSH_8to11().x));
}
#else
fn readSHData(sh: ptr<function, array<half3, 15>>, scale: ptr<function, f32>) {
    let a = fetchScale(loadSplatSH_1to3()); *scale = a.scale;
    sh[0] = half3(a.a); sh[1] = half3(a.b); sh[2] = half3(a.c);
    let b = fetch4(loadSplatSH_4to7());
    sh[3] = half3(b.a); sh[4] = half3(b.b); sh[5] = half3(b.c); sh[6] = half3(b.d);
    let c = fetch4(loadSplatSH_8to11());
    sh[7] = half3(c.a); sh[8] = half3(c.b); sh[9] = half3(c.c); sh[10] = half3(c.d);
    let d = fetch4(loadSplatSH_12to15());
    sh[11] = half3(d.a); sh[12] = half3(d.b); sh[13] = half3(d.c); sh[14] = half3(d.d);
}
#endif
#endif
`

export const GSPLAT_COMPRESSED_SH_GLSL = /* glsl */ `
#if SH_BANDS > 0
vec4 unpack8888s(in uint bits) {
    return vec4((uvec4(bits) >> uvec4(0u, 8u, 16u, 24u)) & 0xffu) * (8.0 / 255.0) - 4.0;
}
void readSHData(out vec3 sh[15], out float scale) {
    uvec4 d0 = loadShTexture0(); uvec4 d1 = loadShTexture1(); uvec4 d2 = loadShTexture2();
    vec4 r0 = unpack8888s(d0.x), r1 = unpack8888s(d0.y), r2 = unpack8888s(d0.z), r3 = unpack8888s(d0.w);
    vec4 g0 = unpack8888s(d1.x), g1 = unpack8888s(d1.y), g2 = unpack8888s(d1.z), g3 = unpack8888s(d1.w);
    vec4 b0 = unpack8888s(d2.x), b1 = unpack8888s(d2.y), b2 = unpack8888s(d2.z), b3 = unpack8888s(d2.w);
    sh[0]=vec3(r0.x,g0.x,b0.x); sh[1]=vec3(r0.y,g0.y,b0.y); sh[2]=vec3(r0.z,g0.z,b0.z); sh[3]=vec3(r0.w,g0.w,b0.w);
    sh[4]=vec3(r1.x,g1.x,b1.x); sh[5]=vec3(r1.y,g1.y,b1.y); sh[6]=vec3(r1.z,g1.z,b1.z); sh[7]=vec3(r1.w,g1.w,b1.w);
    sh[8]=vec3(r2.x,g2.x,b2.x); sh[9]=vec3(r2.y,g2.y,b2.y); sh[10]=vec3(r2.z,g2.z,b2.z); sh[11]=vec3(r2.w,g2.w,b2.w);
    sh[12]=vec3(r3.x,g3.x,b3.x); sh[13]=vec3(r3.y,g3.y,b3.y); sh[14]=vec3(r3.z,g3.z,b3.z); scale=1.0;
}
#endif
`

export const GSPLAT_COMPRESSED_SH_WGSL = /* wgsl */ `
#if SH_BANDS > 0
fn unpack8888s(bits: u32) -> half4 {
    let u = (vec4<u32>(bits) >> vec4<u32>(0u, 8u, 16u, 24u)) & vec4<u32>(0xffu);
    return half4(vec4f(u) * (8.0 / 255.0) - 4.0);
}
fn readSHData(sh: ptr<function, array<half3, 15>>, scale: ptr<function, f32>) {
    let d0=loadShTexture0(); let d1=loadShTexture1(); let d2=loadShTexture2();
    let r0=unpack8888s(d0.x); let r1=unpack8888s(d0.y); let r2=unpack8888s(d0.z); let r3=unpack8888s(d0.w);
    let g0=unpack8888s(d1.x); let g1=unpack8888s(d1.y); let g2=unpack8888s(d1.z); let g3=unpack8888s(d1.w);
    let b0=unpack8888s(d2.x); let b1=unpack8888s(d2.y); let b2=unpack8888s(d2.z); let b3=unpack8888s(d2.w);
    sh[0]=half3(r0.x,g0.x,b0.x); sh[1]=half3(r0.y,g0.y,b0.y); sh[2]=half3(r0.z,g0.z,b0.z); sh[3]=half3(r0.w,g0.w,b0.w);
    sh[4]=half3(r1.x,g1.x,b1.x); sh[5]=half3(r1.y,g1.y,b1.y); sh[6]=half3(r1.z,g1.z,b1.z); sh[7]=half3(r1.w,g1.w,b1.w);
    sh[8]=half3(r2.x,g2.x,b2.x); sh[9]=half3(r2.y,g2.y,b2.y); sh[10]=half3(r2.z,g2.z,b2.z); sh[11]=half3(r2.w,g2.w,b2.w);
    sh[12]=half3(r3.x,g3.x,b3.x); sh[13]=half3(r3.y,g3.y,b3.y); sh[14]=half3(r3.z,g3.z,b3.z); *scale=1.0;
}
#endif
`

export const GSPLAT_SOG_SH_GLSL = /* glsl */ `
#if SH_BANDS > 0
    #ifndef SOG_V2
        uniform float shN_mins;
        uniform float shN_maxs;
    #endif

    void readSHData(out vec3 sh[SH_COEFFS], out float scale) {
        // extract the 16-bit centroid palette index from sh_labels bytes 0 and 1
        vec4 labels = texelFetch(sh_labels, splat.uv, 0);
        int n = int(labels.x * 255.0 + 0.5) + int(labels.y * 255.0 + 0.5) * 256;

        // calculate offset into the centroids texture (64 centroids per row, SH_COEFFS texels each)
        int u = (n % 64) * SH_COEFFS;
        int v = n / 64;

        // read consecutive texels for this centroid
        for (int i = 0; i < SH_COEFFS; i++) {
            vec4 t = texelFetch(sh_centroids, ivec2(u + i, v), 0);
            #ifdef SOG_V2
                ivec3 idx = ivec3(t.xyz * 255.0 + 0.5);
                sh[i] = vec3(lutShN(idx.x), lutShN(idx.y), lutShN(idx.z));
            #else
                sh[i] = mix(vec3(shN_mins), vec3(shN_maxs), t.xyz);
            #endif
        }

        scale = 1.0;
    }
#endif
`

export const GSPLAT_SOG_SH_WGSL = /* wgsl */ `
#if SH_BANDS > 0
    #ifndef SOG_V2
        uniform shN_mins: f32;
        uniform shN_maxs: f32;
    #endif

    fn readSHTexel(u: i32, v: i32) -> half3 {
        let t = textureLoad(sh_centroids, vec2i(u, v), 0);
        #ifdef SOG_V2
            let idx = vec3i(t.xyz * 255.0 + 0.5);
            return half3(vec3f(lutShN(idx.x), lutShN(idx.y), lutShN(idx.z)));
        #else
            return mix(half3(half(uniform.shN_mins)), half3(half(uniform.shN_maxs)), half3(t.xyz));
        #endif
    }

    fn readSHData(sh: ptr<function, array<half3, SH_COEFFS>>, scale: ptr<function, f32>) {
        // extract the 16-bit centroid palette index from sh_labels bytes 0 and 1
        let labels = textureLoad(sh_labels, splat.uv, 0);
        let n = i32(labels.x * 255.0 + 0.5) + i32(labels.y * 255.0 + 0.5) * 256;

        // calculate offset into the centroids texture (64 centroids per row, SH_COEFFS texels each)
        let u = (n % 64) * SH_COEFFS;
        let v = n / 64;

        // read consecutive texels (unrolled to avoid Dawn's forward progress volatile wrapper)
        sh[0] = readSHTexel(u, v);
        sh[1] = readSHTexel(u + 1, v);
        sh[2] = readSHTexel(u + 2, v);
        #if SH_BANDS > 1
            sh[3] = readSHTexel(u + 3, v);
            sh[4] = readSHTexel(u + 4, v);
            sh[5] = readSHTexel(u + 5, v);
            sh[6] = readSHTexel(u + 6, v);
            sh[7] = readSHTexel(u + 7, v);
        #endif
        #if SH_BANDS > 2
            sh[8]  = readSHTexel(u + 8, v);
            sh[9]  = readSHTexel(u + 9, v);
            sh[10] = readSHTexel(u + 10, v);
            sh[11] = readSHTexel(u + 11, v);
            sh[12] = readSHTexel(u + 12, v);
            sh[13] = readSHTexel(u + 13, v);
            sh[14] = readSHTexel(u + 14, v);
        #endif

        *scale = 1.0;
    }
#endif
`

export const GSPLAT_PACKING_GLSL = /* glsl */ `
uint pack8888(vec4 v) {
    uvec4 t = uvec4(v * 255.0) << uvec4(24u, 16u, 8u, 0u);
    return t.x | t.y | t.z | t.w;
}

uint pack101010(vec3 v) {
    uvec3 t = uvec3(v * 1023.0) << uvec3(20u, 10u, 0u);
    return t.x | t.y | t.z;
}

uint pack111110(vec3 v) {
    uvec3 t = uvec3(v * vec3(2047.0, 2047.0, 1023.0)) << uvec3(21u, 10u, 0u);
    return t.x | t.y | t.z;
}

vec4 unpack8888(uint v) {
    return vec4((uvec4(v) >> uvec4(24u, 16u, 8u, 0u)) & 0xffu) / 255.0;
}

vec3 unpack101010(uint v) {
    return vec3((uvec3(v) >> uvec3(20u, 10u, 0u)) & 0x3ffu) / 1023.0;
}

vec3 unpack111110(uint v) {
    return vec3((uvec3(v) >> uvec3(21u, 10u, 0u)) & uvec3(0x7ffu, 0x7ffu, 0x3ffu)) / vec3(2047.0, 2047.0, 1023.0);
}

// resolve the sample using the supplied codebook and return a normalized value relative to codebook min and max
vec3 resolveCodebook(vec3 s, vec4 codebook[64]) {
    uvec3 idx = uvec3(s * 255.0);
    vec3 v = vec3(
        codebook[idx.x >> 2u][idx.x & 3u],
        codebook[idx.y >> 2u][idx.y & 3u],
        codebook[idx.z >> 2u][idx.z & 3u]
    );
    return (v - codebook[0].x) / (codebook[63].w - codebook[0].x);
}
`

export const GSPLAT_PACKING_WGSL = /* wgsl */ `
fn pack8888(v: vec4f) -> u32 {
    let t = vec4u(v * 255.0) << vec4u(24u, 16u, 8u, 0u);
    return t.x | t.y | t.z | t.w;
}

fn pack101010(v: vec3f) -> u32 {
    let t = vec3u(v * vec3f(1023.0, 1023.0, 1023.0)) << vec3u(20u, 10u, 0u);
    return t.x | t.y | t.z;
}

fn pack111110(v: vec3f) -> u32 {
    let t = vec3u(v * vec3f(2047.0, 2047.0, 1023.0)) << vec3u(21u, 10u, 0u);
    return t.x | t.y | t.z;
}

fn unpack8888(v: u32) -> vec4f {
    return vec4f((vec4u(v) >> vec4u(24u, 16u, 8u, 0u)) & vec4u(0xffu)) / 255.0;
}

fn unpack101010(v: u32) -> vec3f {
    return vec3f((vec3u(v) >> vec3u(20u, 10u, 0u)) & vec3u(0x3ffu)) / 1023.0;
}

fn unpack111110(v: u32) -> vec3f {
    return vec3f((vec3u(v) >> vec3u(21u, 10u, 0u)) & vec3u(0x7ffu, 0x7ffu, 0x3ffu)) / vec3f(2047.0, 2047.0, 1023.0);
}`

export const GSPLAT_SOURCE_FORMAT_CHUNKS = Object.freeze({
  glsl: Object.freeze({
    gsplatCompressedSHVS: GSPLAT_COMPRESSED_SH_GLSL,
    gsplatCompressedVS: GSPLAT_COMPRESSED_GLSL,
    gsplatPackingPS: GSPLAT_PACKING_GLSL,
    gsplatSogSHVS: GSPLAT_SOG_SH_GLSL,
    gsplatSogVS: GSPLAT_SOG_GLSL,
    gsplatUncompressedSHVS: GSPLAT_UNCOMPRESSED_SH_GLSL,
    gsplatUncompressedVS: GSPLAT_UNCOMPRESSED_GLSL,
  }),
  wgsl: Object.freeze({
    gsplatCompressedSHVS: GSPLAT_COMPRESSED_SH_WGSL,
    gsplatCompressedVS: GSPLAT_COMPRESSED_WGSL,
    gsplatPackingPS: GSPLAT_PACKING_WGSL,
    gsplatSogSHVS: GSPLAT_SOG_SH_WGSL,
    gsplatSogVS: GSPLAT_SOG_WGSL,
    gsplatUncompressedSHVS: GSPLAT_UNCOMPRESSED_SH_WGSL,
    gsplatUncompressedVS: GSPLAT_UNCOMPRESSED_WGSL,
  }),
})
